'use client';

import { c as _c } from "react/compiler-runtime";
import { TabGroup, Tabs } from '@morpho-org/ui-v6/client';
import { parseAsStringEnum, useQueryState } from 'nuqs';
import { type CSSProperties, type ReactNode, useEffect } from 'react';
import { ScrollSection } from '@/components/common/ScrollSection';
import SubTabsStickyBackground from '@/components/common/SubTabsStickyBackground';
import { useWeb3Context } from '@/contexts/Web3Context';
import { MarketPageTabValues } from '@/helpers/routing';
import { useSectionScrollSpy } from '@/hooks/useSectionScrollSpy';
import { useTrackTabChanged } from '@/lib/analytics/client/hooks/useTrackTabChanged';
import SectionLayout from '../layout/SectionLayout/SectionLayout';
import { getVisibleOptions, getVisibleSections, type MarketSubTabs } from './MarketTabs/useMarketSubTabState';
interface MarketPageMobileLayoutProps {
  heroSlot: ReactNode;
  calloutSlot?: ReactNode;
  simpleActionPanelSlot: ReactNode;
  yourPositionSlots: ReactNode;
  marketSlots: ReactNode;
  ratesSlots: ReactNode;
  riskSlots: ReactNode;
  activitySlots: ReactNode;
}
const MOBILE_NAVBAR_OFFSET = 64;
const MOBILE_PRIMARY_TABS_HEIGHT = 53; // matches CSS --mobile-tabs-height
const MOBILE_SUB_TABS_HEIGHT = 64;
export default function MarketPageMobileLayout(t0) {
  const $ = _c(60);
  const {
    heroSlot,
    calloutSlot,
    simpleActionPanelSlot,
    yourPositionSlots,
    marketSlots,
    ratesSlots,
    riskSlots,
    activitySlots
  } = t0;
  const {
    account: t1
  } = useWeb3Context();
  const {
    isConnected
  } = t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = parseAsStringEnum(Object.values(MarketPageTabValues));
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [activeTab, setActiveTab] = useQueryState("tab", t2);
  const stickyOffset = MOBILE_NAVBAR_OFFSET + MOBILE_PRIMARY_TABS_HEIGHT + MOBILE_SUB_TABS_HEIGHT;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getVisibleSections(false);
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const sections = t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getVisibleOptions(false);
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const filteredOptions = t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      sections,
      defaultSection: "market",
      stickyOffset
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    activeSection,
    scrollToSection,
    registerSection
  } = useSectionScrollSpy(t5);
  const trackTabChanged = useTrackTabChanged("market");
  let t6;
  let t7;
  if ($[4] !== activeTab || $[5] !== isConnected || $[6] !== setActiveTab) {
    t6 = () => {
      if (!activeTab) {
        setActiveTab(MarketPageTabValues.Market);
        return;
      }
      if (!isConnected && activeTab === MarketPageTabValues.MyPosition) {
        setActiveTab(MarketPageTabValues.Market);
      }
    };
    t7 = [isConnected, activeTab, setActiveTab];
    $[4] = activeTab;
    $[5] = isConnected;
    $[6] = setActiveTab;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  const t8 = activeTab ?? undefined;
  let t9;
  if ($[9] !== setActiveTab) {
    t9 = value => setActiveTab(value as MarketPageTabValues);
    $[9] = setActiveTab;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Tabs.Trigger value={MarketPageTabValues.Market}>Market</Tabs.Trigger>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== isConnected) {
    t11 = isConnected && <Tabs.Trigger value={MarketPageTabValues.MyPosition}>My Position</Tabs.Trigger>;
    $[12] = isConnected;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const t12 = isConnected ? "Manage position" : "Open position";
  let t13;
  if ($[14] !== t12) {
    t13 = <Tabs.Trigger value={MarketPageTabValues.ManagePosition}>{t12}</Tabs.Trigger>;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t11 || $[17] !== t13) {
    t14 = <div className="pt-sm mb-sm sticky top-[calc(var(--navbar-height)-1px)] z-2 h-(--mobile-tabs-height) backdrop-blur-[8px]"><Tabs.List aria-label="Navigation">{t10}{t11}{t13}</Tabs.List></div>;
    $[16] = t11;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      "--sticky-offset": `${stickyOffset}px`
    };
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== scrollToSection || $[21] !== trackTabChanged) {
    t16 = tab => {
      trackTabChanged(tab);
      scrollToSection(tab);
    };
    $[20] = scrollToSection;
    $[21] = trackTabChanged;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== activeSection || $[24] !== t16) {
    t17 = <SubTabsStickyBackground topPosition={0} data-testid="market-tabs"><TabGroup id="mobile-sub-tabs" options={filteredOptions} value={activeSection} onChange={t16} /></SubTabsStickyBackground>;
    $[23] = activeSection;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== marketSlots || $[27] !== registerSection) {
    t18 = <ScrollSection id="market" label="Market" stickyOffset={stickyOffset} onRegister={registerSection}>{marketSlots}</ScrollSection>;
    $[26] = marketSlots;
    $[27] = registerSection;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== ratesSlots || $[30] !== registerSection) {
    t19 = <ScrollSection id="rates" label="Rates" stickyOffset={stickyOffset} onRegister={registerSection}>{ratesSlots}</ScrollSection>;
    $[29] = ratesSlots;
    $[30] = registerSection;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== registerSection || $[33] !== riskSlots) {
    t20 = <ScrollSection id="risk" label="Risk" stickyOffset={stickyOffset} onRegister={registerSection}>{riskSlots}</ScrollSection>;
    $[32] = registerSection;
    $[33] = riskSlots;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== activitySlots || $[36] !== registerSection) {
    t21 = <ScrollSection id="activity" label="Activity" stickyOffset={stickyOffset} onRegister={registerSection}>{activitySlots}</ScrollSection>;
    $[35] = activitySlots;
    $[36] = registerSection;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== calloutSlot || $[39] !== heroSlot || $[40] !== t17 || $[41] !== t18 || $[42] !== t19 || $[43] !== t20 || $[44] !== t21) {
    t22 = <Tabs.Content value={MarketPageTabValues.Market} forceMount={true} className="data-[state=inactive]:hidden"><div className="gap-lg mt-[60px] flex flex-col" style={t15 as CSSProperties}>{heroSlot}{calloutSlot}{t17}{t18}{t19}{t20}{t21}</div></Tabs.Content>;
    $[38] = calloutSlot;
    $[39] = heroSlot;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== isConnected || $[47] !== yourPositionSlots) {
    t23 = isConnected && <Tabs.Content value={MarketPageTabValues.MyPosition}>{yourPositionSlots}</Tabs.Content>;
    $[46] = isConnected;
    $[47] = yourPositionSlots;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== simpleActionPanelSlot) {
    t24 = <Tabs.Content value={MarketPageTabValues.ManagePosition}>{simpleActionPanelSlot}</Tabs.Content>;
    $[49] = simpleActionPanelSlot;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t22 || $[52] !== t23 || $[53] !== t24) {
    t25 = <SectionLayout hasLargerVerticalPadding={true} noBackground={true}><div className="flex w-full flex-col gap-[64px]">{t22}{t23}{t24}</div></SectionLayout>;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t14 || $[56] !== t25 || $[57] !== t8 || $[58] !== t9) {
    t26 = <div className="flex-1"><Tabs.Root orientation="horizontal" value={t8} onValueChange={t9}>{t14}{t25}</Tabs.Root></div>;
    $[55] = t14;
    $[56] = t25;
    $[57] = t8;
    $[58] = t9;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  return t26;
}